import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PushTemplate, getPushTemplates, updatePushTemplate } from '../../api';
import './PushTemplateTab.css';

// ─── 표시 규칙 ──────────────────────────────────────────────────────────────
// 카테고리 이름·색은 push_notification_tag(tagName, tagColor)를 그대로 쓰고, 정렬 순서만 여기서 정한다.
// 순서: notification-category-rule.md §3 (가입 → 이용권 → 활동 → 소통)
const CATEGORY_ORDER = ['MEMBER', 'PAYMENT', 'MEMBERSHIP', 'HOLDING', 'LOCKER', 'PRODUCT', 'LESSON', 'EXERCISE', 'POINT', 'LOUNGE'];

type Target = 'MEMBER' | 'COACH' | 'ALL';
const TARGET_LABEL: Record<Target, string> = { MEMBER: '회원', COACH: '관리자·강사', ALL: '회원·관리자·강사' };
const TARGET_ORDER: Target[] = ['MEMBER', 'COACH', 'ALL'];

type Timing = 'EVENT' | 'SCHEDULE';
const TIMING_LABEL: Record<Timing, string> = { EVENT: '이벤트', SCHEDULE: '스케줄' };

type ActiveKey = 'ACTIVE' | 'INACTIVE';
const ACTIVE_LABEL: Record<ActiveKey, string> = { ACTIVE: '활성화', INACTIVE: '비활성화' };

// project가 Link_Scheduler 단독이면 스케줄, 나머지는 이벤트
const timingOf = (t: PushTemplate): Timing => (t.project || '').trim() === 'Link_Scheduler' ? 'SCHEDULE' : 'EVENT';
// project는 "Link_Dev_Back, Link_Coach_Server"처럼 쉼표로 여러 개
const projectsOf = (t: PushTemplate): string[] => (t.project || '').split(',').map(p => p.trim()).filter(Boolean);
const variablesOf = (t: PushTemplate): string[] => (t.availableVariables || '').split(',').map(v => v.trim()).filter(v => v && v !== '-');
const activeOf = (t: PushTemplate): ActiveKey => t.isActive ? 'ACTIVE' : 'INACTIVE';
const categoryRank = (code: string | null) => {
  const i = CATEGORY_ORDER.indexOf((code || '').toUpperCase());
  return i === -1 ? CATEGORY_ORDER.length : i;
};

// 테스트 발송은 아직 없음 — 결과는 전부 미실행으로 표시
type TestResult = 'PASS' | 'FAIL' | 'NONE';
const testResultOf = (_t: PushTemplate, _os: 'ios' | 'android'): TestResult => 'NONE';

// ─── 작은 표시 컴포넌트 ──────────────────────────────────────────────────────
function CategoryChip({ t }: { t: Pick<PushTemplate, 'tagName' | 'tagColor' | 'category'> }) {
  const color = t.tagColor || '#8a9590';
  return (
    <span className="pt-cat" style={{ color, background: color + '22', borderColor: color + '55' }}>
      {t.tagName || t.category || '-'}
    </span>
  );
}

function TargetBadges({ target }: { target: PushTemplate['target'] }) {
  if (!target) return <span className="pt-muted">-</span>;
  return (
    <span className="pt-badges">
      {(target === 'MEMBER' || target === 'ALL') && <span className="pt-badge member">회원</span>}
      {(target === 'COACH' || target === 'ALL') && <span className="pt-badge coach">관리자·강사</span>}
    </span>
  );
}

function TestBadge({ result }: { result: TestResult }) {
  if (result === 'PASS') return <span className="pt-test pass">PASS</span>;
  if (result === 'FAIL') return <span className="pt-test fail">FAIL</span>;
  return <span className="pt-test none">미실행</span>;
}

// {변수} 부분만 강조 — textarea 뒤에 같은 글자를 깔아 색만 입힌다
function renderHighlighted(text: string) {
  const parts = text.split(/(\{[^{}\n]+\})/g);
  return parts.map((p, i) => /^\{[^{}\n]+\}$/.test(p)
    ? <mark key={i} className="pt-var"><span className="pt-brace">{'{'}</span>{p.slice(1, -1)}<span className="pt-brace">{'}'}</span></mark>
    : <React.Fragment key={i}>{p}</React.Fragment>);
}

const HighlightTextarea = React.forwardRef<HTMLTextAreaElement, {
  value: string;
  onChange: (v: string) => void;
  onFocus?: () => void;
  rows?: number;
  placeholder?: string;
}>(({ value, onChange, onFocus, rows = 3, placeholder }, ref) => {
  const backRef = useRef<HTMLDivElement>(null);
  const syncScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
    if (backRef.current) backRef.current.scrollTop = e.currentTarget.scrollTop;
  };
  return (
    <div className="pt-hl">
      <div ref={backRef} className="pt-hl-back" aria-hidden="true">{renderHighlighted(value)}{'\n'}</div>
      <textarea
        ref={ref}
        className="pt-hl-input"
        value={value}
        rows={rows}
        placeholder={placeholder}
        spellCheck={false}
        onChange={e => onChange(e.target.value)}
        onFocus={onFocus}
        onScroll={syncScroll}
      />
    </div>
  );
});

// ─── 필터 ────────────────────────────────────────────────────────────────────
type FilterKey = 'project' | 'category' | 'target' | 'timing' | 'active';
type Filters = Record<FilterKey, string[]>;
const EMPTY_FILTERS: Filters = { project: [], category: [], target: [], timing: [], active: [] };

const matchesFilter = (t: PushTemplate, key: FilterKey, values: string[]): boolean => {
  if (values.length === 0) return true;
  switch (key) {
    case 'project': return projectsOf(t).some(p => values.includes(p));
    case 'category': return values.includes(t.tagCode || t.category);
    case 'target': return values.includes(t.target || '');
    case 'timing': return values.includes(timingOf(t));
    case 'active': return values.includes(activeOf(t));
  }
};

// ─── 정렬 ────────────────────────────────────────────────────────────────────
type SortKey = 'category' | 'templateCode' | 'target' | 'timing' | 'title' | 'body' | 'active' | 'ios' | 'android';
const sortValue = (t: PushTemplate, key: SortKey): string | number => {
  switch (key) {
    case 'category': return categoryRank(t.tagCode || t.category);
    case 'templateCode': return t.templateCode;
    case 'target': return t.target ? TARGET_ORDER.indexOf(t.target) : 99;
    case 'timing': return timingOf(t);
    case 'title': return t.title || '';
    case 'body': return t.body || '';
    case 'active': return t.isActive ? 0 : 1;
    case 'ios': return testResultOf(t, 'ios');
    case 'android': return testResultOf(t, 'android');
  }
};

const COLUMNS: { key: SortKey; label: string; width?: number }[] = [
  { key: 'timing', label: '발송 시점', width: 90 },
  { key: 'category', label: '카테고리', width: 96 },
  { key: 'target', label: '수신인', width: 130 },
  { key: 'templateCode', label: '템플릿 코드', width: 200 },
  { key: 'title', label: '알림 제목' },
  { key: 'body', label: '알림 본문' },
  { key: 'active', label: '사용 여부', width: 84 },
  { key: 'ios', label: 'iOS', width: 72 },
  { key: 'android', label: 'Android', width: 82 },
];

// ─── 메인 ────────────────────────────────────────────────────────────────────
export default function PushTemplateTab() {
  const [templates, setTemplates] = useState<PushTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'category', dir: 1 });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<PushTemplate | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getPushTemplates()
      .then(list => setTemplates(Array.isArray(list) ? list : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // 필터 칩 목록과 건수 (건수는 전체 기준)
  const filterRows = useMemo(() => {
    const count = (fn: (t: PushTemplate) => string[]) => {
      const m = new Map<string, number>();
      templates.forEach(t => fn(t).forEach(v => m.set(v, (m.get(v) || 0) + 1)));
      return m;
    };
    const projectCount = count(projectsOf);
    const categoryMeta = new Map<string, PushTemplate>();
    templates.forEach(t => { const k = t.tagCode || t.category; if (k && !categoryMeta.has(k)) categoryMeta.set(k, t); });
    const categoryCount = count(t => [t.tagCode || t.category].filter(Boolean) as string[]);
    const targetCount = count(t => t.target ? [t.target] : []);
    const timingCount = count(t => [timingOf(t)]);
    const activeCount = count(t => [activeOf(t)]);

    return [
      { key: 'project' as FilterKey, label: '프로젝트', options: Array.from(projectCount.keys()).sort().map(v => ({ value: v, label: v, count: projectCount.get(v)!, mono: true })) },
      { key: 'timing' as FilterKey, label: '발송 시점', options: (['EVENT', 'SCHEDULE'] as Timing[]).filter(v => timingCount.has(v)).map(v => ({ value: v, label: TIMING_LABEL[v], count: timingCount.get(v)!, mono: false })) },
      { key: 'category' as FilterKey, label: '카테고리', options: Array.from(categoryCount.keys()).sort((a, b) => categoryRank(a) - categoryRank(b) || a.localeCompare(b)).map(v => ({ value: v, label: categoryMeta.get(v)?.tagName || v, count: categoryCount.get(v)!, mono: false })) },
      { key: 'target' as FilterKey, label: '수신인', options: TARGET_ORDER.filter(v => targetCount.has(v)).map(v => ({ value: v, label: TARGET_LABEL[v], count: targetCount.get(v)!, mono: false })) },
      { key: 'active' as FilterKey, label: '사용 여부', options: (['ACTIVE', 'INACTIVE'] as ActiveKey[]).filter(v => activeCount.has(v)).map(v => ({ value: v, label: ACTIVE_LABEL[v], count: activeCount.get(v)!, mono: false })) },
    ];
  }, [templates]);

  const visible = useMemo(() => {
    const keys = Object.keys(filters) as FilterKey[];
    const list = templates.filter(t => keys.every(k => matchesFilter(t, k, filters[k])));
    return list.sort((a, b) => {
      const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
      const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return c !== 0 ? c * sort.dir : a.templateCode.localeCompare(b.templateCode);
    });
  }, [templates, filters, sort]);

  const activeFilterCount = Object.values(filters).reduce((n, v) => n + v.length, 0);
  const toggleFilter = (key: FilterKey, value: string) => {
    setFilters(f => ({ ...f, [key]: f[key].includes(value) ? f[key].filter(v => v !== value) : [...f[key], value] }));
  };
  const toggleSort = (key: SortKey) => setSort(s => s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 });

  // 요약 카드
  const total = templates.length;
  const activeCount = templates.filter(t => t.isActive).length;
  const scheduleCount = templates.filter(t => timingOf(t) === 'SCHEDULE').length;
  const iosFail = templates.filter(t => testResultOf(t, 'ios') === 'FAIL').length;
  const androidFail = templates.filter(t => testResultOf(t, 'android') === 'FAIL').length;
  const testFailCount = templates.filter(t => testResultOf(t, 'ios') === 'FAIL' || testResultOf(t, 'android') === 'FAIL').length;

  const openDetail = (t: PushTemplate) => { setSelectedId(t.id); setDetail(t); };

  return (
    <div className="pt-root">
      <div className="pt-cards">
        <div className="pt-card">
          <div className="pt-card-label">활성화 중인 알림</div>
          <div className="pt-card-value"><strong className="accent">{activeCount}</strong><span>비활성화 {total - activeCount}</span></div>
        </div>
        <div className="pt-card">
          <div className="pt-card-label">스케줄 발송</div>
          <div className="pt-card-value"><strong>{scheduleCount}</strong><span>이벤트 발송 {total - scheduleCount}</span></div>
        </div>
        <div className="pt-card">
          <div className="pt-card-label">최근 테스트 실패</div>
          <div className="pt-card-value"><strong className={testFailCount > 0 ? 'warn' : ''}>{testFailCount}</strong><span>iOS {iosFail} · Android {androidFail}</span></div>
        </div>
      </div>

      <div className="pt-panel pt-filters">
        {filterRows.map(row => (
          <div key={row.key} className="pt-filter-row">
            <div className="pt-filter-label">{row.label}</div>
            <div className="pt-filter-chips">
              {row.options.map(o => (
                <button
                  key={o.value}
                  className={`pt-chip ${filters[row.key].includes(o.value) ? 'on' : ''} ${o.mono ? 'mono' : ''}`}
                  onClick={() => toggleFilter(row.key, o.value)}
                >
                  {o.label}<span className="pt-chip-count">{o.count}</span>
                </button>
              ))}
              {row.options.length === 0 && <span className="pt-muted">-</span>}
            </div>
          </div>
        ))}
        <div className="pt-filter-foot">
          <span><strong>{visible.length}건</strong> · {activeFilterCount === 0 ? '전체 보기' : `필터 ${activeFilterCount}개 적용`}</span>
          <button className="pt-btn ghost sm" onClick={() => setFilters(EMPTY_FILTERS)} disabled={activeFilterCount === 0}>필터 초기화</button>
        </div>
      </div>

      <div className="pt-panel pt-table-wrap">
        {loading ? (
          <div className="pt-empty">로딩중...</div>
        ) : visible.length === 0 ? (
          <div className="pt-empty">{total === 0 ? '템플릿이 없습니다.' : '조건에 맞는 알림이 없습니다.'}</div>
        ) : (
          <table className="pt-table">
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th key={c.key} style={c.width ? { width: c.width } : undefined} onClick={() => toggleSort(c.key)}>
                    {c.label}<span className={`pt-sort ${sort.key === c.key ? 'on' : ''}`}>{sort.key === c.key ? (sort.dir === 1 ? '▲' : '▼') : '⇅'}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(t => (
                <tr key={t.id} className={selectedId === t.id ? 'selected' : ''} onClick={() => openDetail(t)}>
                  <td>{TIMING_LABEL[timingOf(t)]}</td>
                  <td><CategoryChip t={t} /></td>
                  <td><TargetBadges target={t.target} /></td>
                  <td className="pt-code">{t.templateCode}</td>
                  <td className="pt-title-cell" title={t.titleEn ? `${t.title}\n${t.titleEn}` : t.title}>
                    <div className="pt-ellipsis">{t.title}</div>
                    {t.titleEn && <div className="pt-ellipsis pt-en">{t.titleEn}</div>}
                  </td>
                  <td className="pt-body-cell" title={t.bodyEn ? `${t.body}\n${t.bodyEn}` : t.body}>
                    <div className="pt-ellipsis">{t.body}</div>
                    {t.bodyEn && <div className="pt-ellipsis pt-en">{t.bodyEn}</div>}
                  </td>
                  <td className={t.isActive ? 'pt-on' : 'pt-off'}>{t.isActive ? '활성화' : '비활성화'}</td>
                  <td><TestBadge result={testResultOf(t, 'ios')} /></td>
                  <td><TestBadge result={testResultOf(t, 'android')} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <PushTemplateDetailModal
          template={detail}
          onClose={() => setDetail(null)}
          onSaved={() => { setDetail(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── 상세 모달 ───────────────────────────────────────────────────────────────
function PushTemplateDetailModal({ template: t, onClose, onSaved }: { template: PushTemplate; onClose: () => void; onSaved: () => void }) {
  const [lang, setLang] = useState<'KR' | 'EN'>('KR');
  const [form, setForm] = useState({
    title: t.title || '',
    body: t.body || '',
    titleEn: t.titleEn || '',
    bodyEn: t.bodyEn || '',
    description: t.description || '',
    isActive: t.isActive,
  });
  const [saving, setSaving] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastFocus = useRef<'title' | 'body'>('body');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const titleKey = lang === 'KR' ? 'title' : 'titleEn';
  const bodyKey = lang === 'KR' ? 'body' : 'bodyEn';
  const variables = variablesOf(t);

  // 마지막으로 포커스한 칸(제목/본문)의 커서 위치에 {변수} 삽입
  const insertVariable = (name: string) => {
    const which = lastFocus.current;
    const el = which === 'title' ? titleRef.current : bodyRef.current;
    const key = which === 'title' ? titleKey : bodyKey;
    const current = form[key];
    const token = `{${name}}`;
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    setForm(f => ({ ...f, [key]: current.slice(0, start) + token + current.slice(end) }));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const canSave = !saving && form.title.trim() !== '' && form.body.trim() !== '';
  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await updatePushTemplate({
        id: t.id,
        title: form.title,
        body: form.body,
        titleEn: form.titleEn,
        bodyEn: form.bodyEn,
        isActive: form.isActive,
        description: form.description,
        location: t.location,
        tagCode: t.tagCode,
        kakaoTemplate: t.kakaoTemplate,
      });
      onSaved();
    } catch (e: any) {
      alert('저장 실패: ' + e.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="pt-modal" onClick={e => e.stopPropagation()}>
        <div className="pt-modal-header">
          <h2>알림 상세</h2>
          <button className="pt-close" onClick={onClose} aria-label="닫기">&times;</button>
        </div>

        <div className="pt-modal-body">
          <div className="pt-grid">
            <div className="pt-field">
              <label>프로젝트</label>
              <div className="pt-readonly">{t.project}</div>
            </div>
            <div className="pt-field">
              <label>카테고리</label>
              <div className="pt-inline"><CategoryChip t={t} /></div>
            </div>
            <div className="pt-field span2">
              <label>템플릿 코드</label>
              <div className="pt-readonly mono">{t.templateCode}</div>
            </div>
            <div className="pt-field">
              <label>수신인</label>
              <div className="pt-inline"><TargetBadges target={t.target} /></div>
            </div>
            <div className="pt-field">
              <label>발송 시점</label>
              <div className="pt-readonly">{TIMING_LABEL[timingOf(t)]}</div>
            </div>
            <div className="pt-field">
              <label>사용 여부</label>
              <div className="pt-segment">
                <button className={form.isActive ? 'on' : ''} onClick={() => setForm(f => ({ ...f, isActive: true }))}>활성화</button>
                <button className={!form.isActive ? 'on' : ''} onClick={() => setForm(f => ({ ...f, isActive: false }))}>비활성화</button>
              </div>
            </div>
            <div className="pt-field">
              <label>최근 테스트</label>
              <div className="pt-inline pt-test-row">
                <span className="pt-test-lg none">iOS 미실행</span>
                <span className="pt-test-lg none">Android 미실행</span>
              </div>
            </div>
          </div>

          <div className="pt-divider" />

          <div className="pt-msg-head">
            <h3>메시지</h3>
            <div className="pt-lang">
              <button className={lang === 'KR' ? 'on' : ''} onClick={() => setLang('KR')}>KR</button>
              <button className={lang === 'EN' ? 'on' : ''} onClick={() => setLang('EN')}>EN</button>
            </div>
          </div>

          <div className="pt-field">
            <label>알림 제목 {lang === 'KR' && <em>*</em>}</label>
            <input
              ref={titleRef}
              className="pt-input"
              value={form[titleKey]}
              placeholder={lang === 'EN' ? '비워 두면 국문으로 발송돼요' : ''}
              onFocus={() => { lastFocus.current = 'title'; }}
              onChange={e => setForm(f => ({ ...f, [titleKey]: e.target.value }))}
            />
          </div>

          <div className="pt-field">
            <label>알림 본문 {lang === 'KR' && <em>*</em>}</label>
            <HighlightTextarea
              ref={bodyRef}
              value={form[bodyKey]}
              rows={4}
              placeholder={lang === 'EN' ? '비워 두면 국문으로 발송돼요' : ''}
              onFocus={() => { lastFocus.current = 'body'; }}
              onChange={v => setForm(f => ({ ...f, [bodyKey]: v }))}
            />
          </div>

          <div className="pt-field">
            <div className="pt-label-row">
              <label>사용 변수</label>
              <span className="pt-hint">누르면 커서 위치에 변수가 들어가요</span>
            </div>
            <div className="pt-var-chips">
              {variables.length === 0
                ? <span className="pt-muted">없음</span>
                : variables.map(v => (
                  <button key={v} className="pt-var-chip" onMouseDown={e => e.preventDefault()} onClick={() => insertVariable(v)}>+ {v}</button>
                ))}
            </div>
          </div>

          <div className="pt-field">
            <label>설명 (발송 조건)</label>
            <input className="pt-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
        </div>

        <div className="pt-modal-foot">
          {/* 테스트 발송은 아직 미구현 — 버튼만 노출 */}
          <button className="pt-btn outline" title="테스트 발송은 준비 중이에요" onClick={() => {}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4 20-7z" /></svg>
            테스트 알림 발송하기
          </button>
          <div className="pt-foot-right">
            <button className="pt-btn ghost" onClick={onClose}>닫기</button>
            <button className="pt-btn primary" onClick={handleSave} disabled={!canSave}>{saving ? '저장중...' : '수정'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
